import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { systemStore } from '../stores/system.store';
import { AlertTriangle, Clock, CheckCircle, Timer } from 'lucide-react';
import { formatDistanceToNow, isPast, parseISO, differenceInSeconds } from 'date-fns';
import { de } from 'date-fns/locale';

export const GlobalMaintenanceBanner = observer(() => {
    const { maintenance, isMaintenanceCompleted } = systemStore;
    const [countdown, setCountdown] = useState<string>('');
    const [isShutdownPending, setIsShutdownPending] = useState(false);
    const [completionTimeLeft, setCompletionTimeLeft] = useState<number>(300);

    useEffect(() => {
        if (!maintenance.shutdownTime) {
            setIsShutdownPending(false);
            return;
        }
        
        const updateTimer = () => {
            const shutdownAt = new Date(maintenance.shutdownTime as string);
            const now = new Date();
            const diff = shutdownAt.getTime() - now.getTime();
            
            if (diff <= 0) {
                setIsShutdownPending(false);
                setCountdown('Server werden heruntergefahren...');
            } else {
                setIsShutdownPending(true);
                const minutes = Math.floor(diff / 60000);
                const seconds = Math.floor((diff % 60000) / 1000);
                setCountdown(`${minutes}:${String(seconds).padStart(2, '0')}`);
            }
        };

        updateTimer();
        const interval = setInterval(updateTimer, 1000);
        return () => clearInterval(interval);
    }, [maintenance.shutdownTime]);

    useEffect(() => {
        if (!maintenance.completed_at) return;
        
        const updateCompletionTimer = () => {
            const completedAt = new Date(maintenance.completed_at as string);
            const elapsed = differenceInSeconds(new Date(), completedAt);
            const remaining = 300 - elapsed;
            
            if (remaining <= 0) {
                systemStore.clearCompletedAt();
                setCompletionTimeLeft(0);
            } else {
                setCompletionTimeLeft(remaining);
            }
        };

        updateCompletionTimer();
        const interval = setInterval(updateCompletionTimer, 1000);
        return () => clearInterval(interval);
    }, [maintenance.completed_at]);

    if (isMaintenanceCompleted && maintenance.completed_at) {
        const minutes = Math.floor(completionTimeLeft / 60);
        const seconds = completionTimeLeft % 60;
        
        return (
            <div className="bg-green-600 text-white px-4 py-3 shadow-lg flex flex-col md:flex-row items-center justify-center gap-2 md:gap-4 animate-in slide-in-from-top duration-300 relative z-50">
                <div className="flex items-center gap-2 font-bold text-white text-sm md:text-base">
                    <CheckCircle size={20} />
                    <span>WARTUNG ABGESCHLOSSEN</span>
                </div>
                
                <span className="text-sm text-green-100">
                    Ihr könnt eure Server wieder starten!
                </span>

                <div className="flex bg-black/20 px-3 py-1 rounded-full text-xs font-mono text-green-100 items-center gap-2">
                    <Clock size={12} />
                    <span>Meldung verschwindet in {minutes}:{String(seconds).padStart(2, '0')}</span>
                </div>
            </div>
        );
    }

    if (!maintenance.active) return null;

    if (isShutdownPending && maintenance.shutdownTime) {
        return (
            <div className="bg-yellow-600 text-white px-4 py-2 shadow-lg flex items-center justify-center gap-4 animate-pulse relative z-50">
                <div className="flex items-center gap-2 font-bold text-white text-sm md:text-base">
                    <Timer size={20} />
                    <span>WARTUNG ANGEKÜNDIGT</span>
                </div>
                
                <div className="flex bg-black/20 px-3 py-1 rounded-full text-sm font-mono font-bold text-yellow-100 items-center gap-2">
                    <Clock size={14} />
                    <span>Server-Shutdown in {countdown}</span>
                </div>

                <span className="text-sm text-yellow-100 hidden sm:inline border-l border-white/20 pl-4">
                    Server-Start ist blockiert
                </span>
            </div>
        );
    }

    return (
        <div className="bg-orange-600 text-white px-4 py-2 shadow-lg flex items-center justify-center gap-4 relative z-50">
            <div className="flex items-center gap-2 font-bold text-white text-sm md:text-base">
                <AlertTriangle className="animate-pulse" size={20} />
                <span>WARTUNGSARBEITEN AKTIV</span>
            </div>
            
            {maintenance.endTime && (
                <div className="hidden md:flex bg-black/20 px-3 py-1 rounded-full text-xs font-mono text-orange-100 items-center gap-2">
                    <Clock size={12} />
                    <span>Geplantes Ende: {formatDistanceToNow(parseISO(maintenance.endTime), { locale: de, addSuffix: true })}</span>
                </div>
            )}

            {maintenance.message && (
                <span className="text-sm text-orange-100 hidden sm:inline border-l border-white/20 pl-4">
                    {maintenance.message}
                </span>
            )}
        </div>
    );
});
